import { formatAcres } from "../utils/acreageFormatting.js";

function formatOverlapArea(area) {
  if (typeof area !== "number" || Number.isNaN(area)) {
    return "Unknown overlap";
  }

  return `${formatAcres(area)} overlapping acres`;
}

function NoticeContainer({ tone, children }) {
  const toneClasses = {
    neutral: "border-[#d6d6d6] bg-[#f6f6f6] text-[#4b5563]",
    loading: "border-[#f59e0b] bg-[#fff7e6] text-[#92400e]",
    success: "border-[#34a853] bg-[#edf8ef] text-[#1f6b35]",
    error: "border-[#ef4444] bg-[#fff1f2] text-[#991b1b]",
    warning: "border-[#f97316] bg-[#fff7ed] text-[#9a3412]",
  };

  return (
    <div
      className={`mb-4 rounded-sm border px-4 py-3 text-sm ${toneClasses[tone]}`}
    >
      {children}
    </div>
  );
}

export default function FieldBoundaryValidationNotice({ validationState }) {
  if (!validationState) {
    return null;
  }

  const {
    status,
    reason,
    conflicts = [],
    message,
    showReferenceHint = true,
  } = validationState;

  if (status === "loading") {
    return (
      <NoticeContainer tone="loading">
        Checking field boundaries for overlap and polygon validity.
      </NoticeContainer>
    );
  }

  if (status === "valid") {
    return (
      <NoticeContainer tone="success">
        Boundaries look good. Other saved fields are shown in electric purple on
        the map for reference.
      </NoticeContainer>
    );
  }

  if (status === "invalid" && reason === "invalid_polygon") {
    return (
      <NoticeContainer tone="warning">
        {message || "This polygon is not valid yet. Please redraw or adjust it."}
      </NoticeContainer>
    );
  }

  if (status === "invalid" && reason === "overlap") {
    return (
      <NoticeContainer tone="error">
        <p className="font-semibold">
          {message ||
            "These boundaries overlap another saved field for this grower."}
        </p>
        <div className="mt-3 flex flex-col gap-2">
          {conflicts.map((conflict) => (
            <div
              key={`${conflict.farm_id}-${conflict.field_id}`}
              className="rounded-sm border border-[#fecaca] bg-white px-3 py-2"
            >
              <p className="font-medium">
                {conflict.field_name} on {conflict.farm_name}
              </p>
              <p className="text-xs">
                {conflict.province || "Province unknown"} •{" "}
                {formatOverlapArea(conflict.overlap_area_acres)}
              </p>
            </div>
          ))}
        </div>
      </NoticeContainer>
    );
  }

  if (status === "error") {
    return (
      <NoticeContainer tone="neutral">
        {message ||
          "Boundary pre-check is temporarily unavailable. Save will still run server-side validation."}
      </NoticeContainer>
    );
  }

  if (showReferenceHint) {
    return (
      <NoticeContainer tone="neutral">
        Other saved fields appear in electric purple on the map so the active
        field stays distinct while you draw.
      </NoticeContainer>
    );
  }

  return null;
}
